/**
 * /settings/jd-templates — Settings → Hiring → JD templates (Wave
 * K1.6; SOP §8.3 — catalog admin lives in Settings, not the module
 * strip).
 *
 * List/create/edit/delete the tenant's reusable job-description
 * templates. Mutations are HR-admin + CSRF + Audit server-side; the
 * page UX-gates to HR admins (backend re-checks).
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useDeleteJDTemplate,
  useJDTemplates,
} from "@/hooks/use-hiring";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { JDTemplateForm } from "@/components/hiring/jd-template-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { JDTemplate } from "@/types/hiring";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/jd-templates",
)({
  component: SettingsJDTemplatesRoute,
});

function SettingsJDTemplatesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useJDTemplates();
  const del = useDeleteJDTemplate();

  const templates = useMemo(
    () =>
      [...(q.data?.jd_templates ?? [])].sort((a, b) =>
        a.title.localeCompare(b.title),
      ),
    [q.data],
  );

  async function onDelete(t: JDTemplate) {
    try {
      await del.mutateAsync(t.id);
      toast.success(`Deleted "${t.title}".`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">JD Templates</h2>
              <p className="text-sm text-muted-foreground">
                Reusable job descriptions — &ldquo;Start from
                template&rdquo; on a new requisition prefills the title,
                department and JD.
              </p>
            </div>
            {isHrAdmin && <JDTemplateForm />}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change JD
                templates.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load JD templates</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && templates.length === 0 && (
            <EmptyState
              icon={FileText}
              title="No JD Templates Yet"
              description={
                isHrAdmin
                  ? "A JD template prefills the title, department and job description on a new requisition. Use New template above, or “Save as template” on a requisition."
                  : "A JD template prefills the title, department and job description on a new requisition. An HR manager or company admin can create them."
              }
            />
          )}

          {q.data && templates.length > 0 && (
            <div className="space-y-3">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">
                          {t.title}
                        </CardTitle>
                        {t.department && (
                          <p className="mt-0.5 text-sm text-muted-foreground">
                            {t.department}
                          </p>
                        )}
                        {t.body_text && (
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                            {t.body_text}
                          </p>
                        )}
                      </div>
                      {isHrAdmin && (
                        <div className="flex shrink-0 gap-2">
                          <JDTemplateForm existing={t} />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete &ldquo;{t.title}&rdquo;?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Requisitions already created from it
                                  are unaffected — only the reusable
                                  template is removed.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => void onDelete(t)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
